import { useState, useRef } from 'react';
import { Button, ConfigProvider, theme } from 'antd';
import { useAuth } from '../../providers/logincontext';
import { showError } from '../../components/utils/notifications';
import PageTransition from '../../components/utils/PageTransition';
import styles from './LoginPage.module.css';
import { ReactComponent as LoginIllustration } from '../../assets/login-illustration.svg';
import scalLogo from '../../assets/scal.png';

const LoginPage = () => {
  const { handleLogin } = useAuth();
  const [loading, setLoading] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!panelRef.current || !imageRef.current) return;

    const rect = panelRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -1;
    const rotateY = ((x - centerX) / centerX) * 1;

    imageRef.current.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  };

  const handleMouseLeave = () => {
    if (imageRef.current) {
      imageRef.current.style.transform = `rotateX(0deg) rotateY(0deg)`;
    }
  };

  const onSsoSignIn = async () => {
    try {
      setLoading(true);
      await handleLogin();
    } catch (error) {
      console.error('SSO sign-in error', error);
      showError('Unable to start SSO sign-in.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition>
      <ConfigProvider
        theme={{
          algorithm: theme.darkAlgorithm,
          token: {
            colorPrimary: '#19E69C',
            colorBgContainer: '#141414',
            colorBorder: '#262626',
            colorText: '#f5f5f5',
            colorTextPlaceholder: '#545454',
            borderRadius: 6,
          },
        }}
      >
        <div className={styles.container} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} data-theme="dark">
          <div className={styles.formSection}>
            <div className={styles.contentWrapper}>
              <div className={styles.brand}>
                <div className={styles.brandLabel}>SCAL</div>
                <p className={styles.subtitle}>Special Core Analytical Platform</p>
                <h1 className={styles.title}>Sign in</h1>
                <p className={styles.subtitle} style={{ marginTop: '1rem' }}>
                  Use your Geowellex account to continue.
                </p>
              </div>

              <Button
                type="primary"
                size="large"
                loading={loading}
                className={styles.loginButton}
                onClick={onSsoSignIn}
                data-testid="sso-login"
              >
                Sign In with SSO
              </Button>
            </div>
          </div>

          <div
            className={styles.sidePanel}
            ref={panelRef}
          >
            <LoginIllustration className={styles.illustration} />
            <h2 className={styles.sidePanelTitle}>Advanced Reservoir Analytics</h2>
            <p className={styles.featureDescription}>
              Comprehensive tools for Kr and Pc data analysis, interpretation, and normalization.
            </p>
            <img ref={imageRef} src={scalLogo} alt="SCAL Logo" className={styles.screen} />
          </div>
        </div>
      </ConfigProvider>
    </PageTransition>
  );
};

export default LoginPage;
